import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Settings, 
  Activity, 
  Users, 
  CheckCircle2, 
  Wrench, 
  Shield, 
  ArrowRight, 
  ClipboardList, 
  FileText 
} from 'lucide-react';

export default function Services() {
  const [servicesPage, setServicesPage] = useState(null);

  useEffect(() => {
    async function fetchServicesPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/ydelser');
        if (res.ok) {
          const data = await res.json();
          setServicesPage(data);
        }
      } catch (err) {
        console.error('Error fetching services page:', err);
      }
    }
    fetchServicesPage();
  }, []);

  const title = "Ydelser - CARPA ApS";
  const desc = "Professionel rådgivning og praktisk eksekvering inden for Commissioning Management, Operational Readiness, Technical Leadership, Handover & Start-up Support og Strategic Maintenance.";

  const servicesList = [
    {
      id: 'commissioning',
      title: 'Commissioning Management',
      icon: Settings,
      desc: 'Planlægning og styring af test, interfaces, punch, dokumentation og systemoverdragelse. Fra mekanisk færdigt anlæg til verificeret funktion.',
      helpsWith: [
        'Udarbejdelse af overordnede commissioning-planer og testprocedurer.',
        'Koordinering af FAT (Factory Acceptance Test) og SAT (Site Acceptance Test).',
        'Styring af testforløb og systematisk dokumentation af alle systemer.',
        'Håndtering af udestående punkter (punch lists) og overdragelsesforretning.'
      ]
    },
    {
      id: 'readiness',
      title: 'Operational Readiness',
      icon: Activity,
      desc: 'Vi gør driftsteam, procedurer, reservedele, dokumentation, CMMS-grundlag og processer klar til overtagelse.',
      helpsWith: [
        'Udarbejdelse af driftsprocedurer, SOP\'er og vedligeholdelsesmanualer.',
        'Etablering af reservedelsstrategier og indkøb af kritiske reservedele.',
        'Planlægning og facilitering af træningsprogrammer for driftspersonale.',
        'Sikring af, at alle lovmæssige godkendelser og dokumenter er på plads.'
      ]
    },
    {
      id: 'leadership',
      title: 'Technical Leadership',
      icon: Users,
      desc: 'Teknisk ledelse tæt på anlægget — med fokus på fremdrift, beslutninger, leverandørstyring og kvalitet.',
      helpsWith: [
        'Teknisk koordinering på tværs af fagdiscipliner (maskin, el, proces, SRO).',
        'Styring af tekniske grænseflader (interfaces) mellem forskellige leverandører.',
        'Teknisk risikostyring og problemløsning i kritiske projektfaser.',
        'Support ved udbud, teknisk evaluering og kontraktforhandlinger.'
      ]
    },
    {
      id: 'handover',
      title: 'Handover & Start-up Support',
      icon: CheckCircle2,
      desc: 'Støtte i overgangen fra projekt til drift, hvor viden, ansvar og ejerskab skal flyttes uden tab af kontrol.',
      helpsWith: [
        'On-site support under de første kritiske opstartsforløb og funktionstests.',
        'Fejlfinding og hurtig afhjælpning af tekniske problemer under opstart.',
        'Verificering af, at anlægget yder i overensstemmelse med performancekrav.',
        'Sikring af en struktureret og dokumenteret overdragelse til driftsorganisationen.'
      ]
    },
    {
      id: 'maintenance',
      title: 'Strategic Maintenance',
      icon: Wrench,
      desc: 'Opbygning af vedligeholdsstruktur, reservedelsstrategi, CMMS-grundlag og praktiske løsninger, der reducerer nedetid.',
      helpsWith: [
        'Opbygning af struktureret vedligehold i CMMS-systemer.',
        'Klassificering og kritikalitetsanalyser af anlægskomponenter.',
        'Udarbejdelse af forebyggende vedligeholdelsesrutiner.',
        'Optimering af reservedelslagre og kritiske reservedelslister.'
      ]
    }
  ];

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen font-sans antialiased">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[60vh] flex flex-col justify-center py-16 md:py-24 overflow-hidden bg-[#0a1520] border-b border-[#0e1f2f]/20 mb-16">
        {/* Background Grid Pattern & Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/237ca15e-31ae-496d-a72d-cf3337102408.jpeg"
            alt="Vores ydelser"
            className="h-full w-full object-cover opacity-35 lg:opacity-45 object-center"
          />
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(23,211,214,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(23,211,214,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
          {/* Gradient Overlay preserving text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1520] via-[#0a1520]/95 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1520]/90 via-[#0a1520]/80 to-[#0a1520] lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Title & Process */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Action/Process Header Link */}
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-mono tracking-wider uppercase">
                <span className="text-[#17d3d6] font-bold">
                  {servicesPage?.hero_badge?.da || servicesPage?.hero_badge || "Planlæg → Test & Verificer → Sikker Drift"}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wide uppercase text-white leading-none">
                  {servicesPage?.hero_title?.da || servicesPage?.hero_title || "Ydelser"}
                </h1>
                <div className="w-16 h-[3px] bg-[#17d3d6] mt-4" />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {servicesPage?.hero_description?.da || servicesPage?.hero_description || "CARPA leverer praktisk, teknisk og driftssikker rådgivning fra projekt til sikker drift. Vi har fokus på praktisk eksekvering, tæt samarbejde og stærk teknisk kontrol."}
              </p>
            </div>

            {/* Right Column: Premium Floating Card with Glow Border */}
            <div className="lg:col-span-5">
              <div className="bg-[#041326]/65 backdrop-blur-md border border-[rgba(23,211,214,0.15)] rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all duration-300 hover:border-[#17d3d6]/40 shadow-2xl">
                <div>
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-700/40">
                    <div className="w-12 h-12 shrink-0 rounded-full border border-[#17d3d6] flex items-center justify-center text-[#17d3d6]">
                      <Settings className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold leading-tight">Ydelser i overblik</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider uppercase mt-0.5">CARPA ApS</p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3.5">
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Commissioning Management fra A-Z
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Operational Readiness & CMMS-struktur
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Teknisk ledelse tæt på anlægget
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Sikker overdragelse til driftsorganisationen
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 space-y-24">

        {/* Pipeline Stepper */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12 py-6 border-y border-[#0e1f2f]/10 bg-white rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#3c8888] text-white text-xs font-bold font-mono">01</span>
            <span className="text-base font-bold text-[#0e1f2f]">Planlægning</span>
          </div>
          <div className="hidden md:block h-[2px] w-16 bg-[#0e1f2f]/10" />
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0e1f2f] text-white text-xs font-bold font-mono">02</span>
            <span className="text-base font-bold text-[#0e1f2f]">Test & Verificering</span>
          </div>
          <div className="hidden md:block h-[2px] w-16 bg-[#0e1f2f]/10" />
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#3c8888]/60 text-white text-xs font-bold font-mono">03</span>
            <span className="text-base font-bold text-[#0e1f2f]">Sikker Drift</span>
          </div>
        </div>

        {/* Detailed Services Grid */}
        <section className="space-y-16 mb-12">
          {servicesList.map((srv, idx) => {
            const Icon = srv.icon;
            return (
              <div 
                key={srv.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start border-b border-[#0e1f2f]/10 pb-16 last:border-0 last:pb-0"
              >
                {/* Left Column: Title, Icon & Desc */}
                <div className="lg:col-span-4 space-y-4">
                  <div className="inline-flex rounded-lg bg-[#3c8888]/10 p-3 text-[#3c8888]">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0e1f2f] tracking-tight">
                    {srv.title}
                  </h2>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    {srv.desc}
                  </p>
                </div>

                {/* Right Column: Detailed helpsWith list */}
                <div className="lg:col-span-8 bg-white rounded-xl border border-[#0e1f2f]/10 p-6 md:p-8 shadow-sm space-y-4">
                  <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-[#3c8888] flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    <span>Hvad vi sikrer i processen</span>
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {srv.helpsWith.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700">
                        <CheckCircle2 className="h-4 w-4 text-[#3c8888] shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </section>

        {/* Risk & Safety Specialist Area */}
        {servicesPage?.show_risk_safety === true && (
          <section className="bg-white rounded-2xl border border-[#0e1f2f]/10 p-8 md:p-12 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex rounded-lg bg-stone-100 p-2.5 text-stone-600">
                <Shield className="h-5 w-5" />
              </div>
              <h3 className="text-2xl font-bold text-[#0e1f2f] tracking-tight">
                {servicesPage?.risk_safety_title?.da || "Risk & Safety (Specialistområde)"}
              </h3>
              {servicesPage?.risk_safety_description?.da ? (
                <div 
                  className="text-stone-600 text-sm sm:text-base leading-relaxed prose max-w-none prose-stone"
                  dangerouslySetInnerHTML={{ __html: servicesPage.risk_safety_description.da }}
                />
              ) : (
                <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                  Vi tilbyder generisk rådgivning og netværksbaseret support inden for funktionel sikkerhed og risikostyring på komplekse tekniske anlæg. Dette dækker bl.a. overordnet risikovurdering og sikkerhedskoordinering i tæt samarbejde med jeres team.
                </p>
              )}
            </div>
            <div className="lg:col-span-4 flex justify-end">
              <Link
                to="/kontakt"
                className="w-full lg:w-auto inline-flex items-center justify-center rounded-md bg-[#0e1f2f] px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-[#0e1f2f]/90 transition-colors border-b-2 border-[#3c8888]"
              >
                <span>{servicesPage?.risk_safety_button_text?.da || "Hør mere om Risk & Safety"}</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
